import PropTypes from 'prop-types'
import {useState, useCallback, useEffect, Fragment} from 'react'
import {connect} from 'react-redux'
import {useForm, FormProvider} from 'react-hook-form'
import {Map} from 'immutable'
import {readEndpoint} from 'redux-json-api'
import {Link} from 'react-router'


import {Button} from '../../../../dashboard/src/components/blocks/Buttons'
import MixpanelLogger from '../../../../lib/MixpanelLogger'
import SignInActions from '../../actions/SignInActions'
import TfaSignInFrame from './TfaSignInFrame'
import TextInput from '../../../../shared_components/forms/v3/TextInput'

import {currentUserConfig} from '../../../../lib/userTools'
import preAuthHook from '../../lib/preAuthHook'
import SessionStore from '../../stores/SessionStore'
import {findRawResourceByAttributeId, findRawResourceById} from '../../../../lib/plan_data/userData'

import './tfaBackupSignIn.scss'

const TfaBackupSignIn = (props, context) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState()
  const session = SessionStore.getState()
  const methods = useForm()
  const {errors} = methods.formState

  const clearErrors = useCallback(() => setError(null), [])

  const onUpdate = useCallback(() => {
    const {currentUser} = session

    if (session.signInError) {
      setLoading(false)
      setError(session.signInError)
    } else if (session.signedInWithBackup) {
      // Prevent 500 when non-client user tries to read this endpoint and also make sure there's a current user data
      props.readEndpoint('user-configs')

      if (currentUser.client)
        props.readEndpoint(`everplans/${session.currentUser.everplan.id}?include=firm.firm-config,firm.organization.organization-config,firm-ownership`)
      else if (currentUser.firm_user)
        props.readEndpoint(`firms/${currentUser.firm}?include=client-seats,firm-config,disclosure,firm-seats,organization.organization-config`)

      setLoading(false)
    }
  }, [session])

  const normalizeBackupCode = value => value.trim().replace(/\u2015/g, '').replace(/\s/g, '')

  const submitBackupCode = useCallback(formData => {
    const email = session.preAuthEmail
    const password = session.preAuthPassword
    const code = normalizeBackupCode(formData.backupCode)

    if (email && password) {
      clearErrors()
      setLoading(true)
      SignInActions.submitBackupCode(email, password, code)
    } else {
      context.router.push('auth/sign_in')
    }
  }, [session])

  useEffect(() => {
    preAuthHook()
    clearErrors()
    SessionStore.addChangeListener(onUpdate)

    return () => {
      SessionStore.removeChangeListener(onUpdate)
    }
  }, [])

  return (
    <TfaSignInFrame
      className='tfa-backup-sign-in'
      heading='Verify your identity'
      subtitle='Please enter your 16-character backup code that you received when you set-up Two-Step Verification.'
      footer={
        <Fragment>
          <Button className='submit' processing={loading} onClick={methods.handleSubmit(submitBackupCode)}>Verify code</Button>
          <Link to='/auth/sign_in/tfa_alternatives' className='alternative-method' onClick={() => MixpanelLogger.track('2fa_try_different_method')}>Try a different method</Link>
        </Fragment>
      }>
      <h5>Backup code</h5>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(submitBackupCode)}>
          <TextInput
            {...methods.register(
              'backupCode',
              {
                required: 'Enter your backup code.',
                validate: value => normalizeBackupCode(value).length === 16 || 'Backup code must be 16 characters.'
              }
            )}
            error={!!errors.backupCode || !!error}
            helperText={errors.backupCode?.message || error}
          />
          <input type='submit' hidden />
        </form>
      </FormProvider>
    </TfaSignInFrame>
  )
}

TfaBackupSignIn.contextTypes = {router: PropTypes.object}

TfaBackupSignIn.propTypes = {
  userConfig: PropTypes.instanceOf(Map),
  organizationConfig: PropTypes.instanceOf(Map),
  loading: PropTypes.bool,
  readEndpoint: PropTypes.func,
  isFuneralHome: PropTypes.bool,
  isQuarterback: PropTypes.bool,
  isFirmUser: PropTypes.bool
}

const mapStateToProps = state => {
  const userConfig = currentUserConfig(state.api)

  const firmOwnership = findRawResourceByAttributeId({
    rawResource: state.api['firm-ownerships'],
    attribute: 'everplan-id',
    id: userConfig.get('everplan-id')
  })

  const firm = findRawResourceById({
    rawResource: state.api.firms,
    id: firmOwnership.get('ownable-id') || userConfig.get('firm-id')
  })

  const organizationConfig = findRawResourceByAttributeId({
    rawResource: state.api['organization-configs'],
    attribute: 'organization-id',
    id: firm.get('organization-id')
  })

  const isFuneralHome = firm.get('firm-type') === 'funeral_home'
  const isQuarterback = userConfig.get('quarterback')
  const isFirmUser = userConfig.get('firm-id') && !userConfig.get('client')

  return {
    userConfig,
    organizationConfig,
    loading: state.api.isReading > 0,
    isFuneralHome,
    isQuarterback,
    isFirmUser
  }
}

export default connect(mapStateToProps, {readEndpoint})(TfaBackupSignIn)
